// One plain English line per claim, for the record pane. The raw predicate is shown beside it.

import type { Claim } from '@model/index.ts';
import { claim, embedded, num, str } from './queries.ts';
import type { World } from './world.ts';
import { fmtDate, fmtPrice } from './format.ts';

const q = (s: string): string => `“${s}”`;

// What someone tried to do, for claims the law refused.
export function attempt(w: World, c: Claim): string {
  const who = w.name(c.author, 'en');
  const subj = w.name(c.subject, 'en');
  const ref = claim(w, c.refs[0]);
  const title = (o: Claim | undefined): string => (o ? q(str(o, 'title')) : 'an item');
  const phrase: Record<string, string> = {
    policy: 'change the rules',
    'verified.phone': `attest ${subj}’s phone number`,
    join: 'join',
    vouch: `vouch for ${subj}`,
    offer: `put up ${q(str(c, 'title') || 'an item')}`,
    withdraw: `take down ${title(ref)}`,
    inquire: ref ? `ask about ${title(ref)}` : 'open a chat about no item',
    accept: `agree to buy ${title(ref)}`,
    complete: `confirm the handover of ${title(claim(w, ref?.refs[0]))}`,
    rate: ref ? `say how it went with ${subj}` : `rate ${subj} without a trade`,
    respond: ref?.predicate === 'decide' ? 'reply to a decision' : 'reply to a rating',
    appeal: 'appeal a decision',
    release: `release the deal for ${title(claim(w, ref?.refs[0]))}`,
    report: `report ${subj}`,
    promote: 'share a private message as proof',
    flag: 'add a triage note',
    decide:
      str(c, 'outcome') === 'suspend-offers'
        ? `pause ${subj}’s items until ${fmtDate(num(c, 'until'), 'en')}`
        : `close the reports about ${subj}`,
  };
  return `${who} tried to ${phrase[c.predicate] ?? c.predicate}`;
}

export function describe(w: World, c: Claim): string {
  const who = w.name(c.author, 'en');
  const subj = w.name(c.subject, 'en');
  const ref = claim(w, c.refs[0]);
  const itemTitle = (offer: Claim | undefined): string => (offer ? q(str(offer, 'title')) : 'an item');
  switch (c.predicate) {
    case 'policy': {
      const policies = w.scope.log.map((e) => e.claim).filter((x) => x.predicate === 'policy');
      const i = policies.findIndex((x) => x.id === c.id);
      if (i <= 0) return `${who} set its rules`;
      const before = (policies[i - 1]!.body as { params?: Record<string, unknown> }).params ?? {};
      const after = (c.body as { params?: Record<string, unknown> }).params ?? {};
      const diff = Object.keys(after)
        .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
        .map((k) => `${k} ${JSON.stringify(before[k])} → ${JSON.stringify(after[k])}`);
      return `${who} changed its rules${diff.length ? `: ${diff.join(', ')}` : ''}`;
    }
    case 'verified.phone':
      return `${who} checked ${subj}’s number and kept nothing`;
    case 'join':
      return `${who} joined`;
    case 'vouch':
      return `${who} vouched for ${subj}`;
    case 'offer':
      return `${who} put up ${q(str(c, 'title'))} for ${fmtPrice(num(c, 'priceInr'), 'en')}`;
    case 'withdraw':
      return `${who} took down ${itemTitle(ref)}`;
    case 'inquire':
      return ref ? `${who} asked ${w.name(ref.author, 'en')} about ${itemTitle(ref)}` : `${who} opened a chat about nothing`;
    case 'accept':
      return `${who} agreed to buy ${itemTitle(ref)} from ${w.name(ref?.author, 'en')}`;
    case 'complete':
      return `${who} confirmed the handover of ${itemTitle(claim(w, ref?.refs[0]))}`;
    case 'rate': {
      const words = ['', 'it went badly', 'there were problems', 'it was okay', 'it went well', 'it went very well'];
      return ref ? `${who} said ${words[num(c, 'score')] ?? '?'} with ${subj}` : `${who} tried to rate ${subj} without a trade`;
    }
    case 'respond':
      return ref?.predicate === 'decide'
        ? `${who} replied on the record to ${w.name(ref.author, 'en')}’s decision`
        : `${who} replied on the record to ${w.name(ref?.author, 'en')}`;
    case 'report':
      return `${who} reported ${subj}`;
    case 'promote': {
      const inner = embedded(c);
      return `${who} shared a private message from ${w.name(inner?.author, 'en')} as proof`;
    }
    case 'flag':
      return `${who} added a triage note (${str(c, 'level') || 'risk'} ${num(c, 'risk').toFixed(2)})`;
    case 'decide': {
      const onAppeal = ref?.predicate === 'appeal';
      const what =
        str(c, 'outcome') === 'suspend-offers'
          ? `paused ${subj}’s items until ${fmtDate(num(c, 'until'), 'en')}`
          : onAppeal
            ? `lifted the pause on ${subj}`
            : `closed the reports about ${subj} with no action`;
      return onAppeal ? `${who} decided ${subj}’s appeal: ${what}` : `${who} ${what}${c.refs.length > 1 ? ` (${c.refs.length} reports)` : ''}`;
    }
    case 'appeal':
      return `${who} appealed ${w.name(ref?.author, 'en')}’s decision`;
    case 'release':
      return `${who} released the deal for ${itemTitle(claim(w, ref?.refs[0]))}`;
    case 'pledge':
      return `${who} pledged ${fmtPrice(num(c, 'amountInr'), 'en')}`;
    default:
      return `${who} said ${c.predicate}`;
  }
}
